/**
 * Rolling auth-event log written to localStorage so we can surface the
 * actual reason a session was lost on the next /login render. Built
 * specifically for the recurring "Google signs me out on Safari mobile"
 * bug — by the time the user lands on /login the in-memory React state
 * is gone, but localStorage survives the redirect.
 *
 * Capped at 20 entries (newest last). Each entry is a small JSON
 * record so the LoginPage diagnostics panel can render it line-by-line.
 *
 * Storage key: `trybeup_auth_log` — read by LoginPage, written by
 * lib/api.ts (refresh attempts), lib/auth.ts (sign-out), and
 * hooks/useAuth.ts (warmer + SIGNED_OUT events).
 *
 * Never throws. localStorage in Safari private mode / quota-exceeded
 * scenarios is allowed to fail silently — diagnostics must never
 * become the thing that breaks the app.
 */

export type AuthEventType =
  | 'initial_session'
  | 'warmer_run'
  | 'warmer_refresh_outcome'
  // Logged when the warmer's first refresh attempt returned auth-failed
  // and we're about to retry after a brief delay to survive the cross-tab
  // refresh-token rotation race.
  | 'warmer_retry_attempt'
  | 'warmer_retry_outcome'
  // Logged when the warmer's retried refresh also failed and we're
  // accepting the sign-out. Surfaces the diagnostic panel on /login.
  | 'warmer_signout'
  | 'session_loading_watchdog'
  | 'refresh_attempt'
  | 'refresh_outcome'
  // Logged when the FIRST refresh attempt returned auth-failed and we're
  // about to retry after the race-recovery delay. Mirrors warmer_retry_*
  // but fires from tryRefreshSession itself, so any caller (interceptor,
  // proactive refresh, apiFetch) gets the same multi-tab/device race
  // survival the warmer used to have on its own.
  | 'refresh_retry_attempt'
  | 'refresh_retry_outcome'
  | 'signout_initiated'
  | 'signed_out_event'
  // Fired when a SIGNED_OUT event arrives without our self-initiated
  // marker but the SDK still reports a live session — we ignore the
  // event and keep the user signed in. Tracks ghost sign-outs the
  // guard caught.
  | 'signed_out_ignored'
  // Fired when an unmarked SIGNED_OUT event survives the verify check
  // (SDK confirms session is genuinely gone). Distinguishes "this
  // sign-out actually happened" from spurious ones for analytics.
  | 'signed_out_confirmed'
  | 'reactive_signout'
  // Durable-storage adapter events. Fired by durableSessionStorage.ts
  // when the GoTrue SDK writes, restores, or removes the session key
  // — exposes silent IDB failures that would otherwise leave us
  // with no mirror to recover from after a Safari localStorage wipe.
  | 'durable_session_write'
  | 'durable_session_restore'
  | 'durable_session_remove'
  // Fired when useAuth's OAuth-callback recovery fallback runs
  // because auth-js's automatic detectSessionInUrl path silently
  // failed. Outcome tells whether the manual setSession() (implicit
  // flow) or exchangeCodeForSession() (PKCE flow) succeeded.
  | 'oauth_recovery';

export interface AuthEvent {
  /** ISO timestamp */
  at: string;
  type: AuthEventType;
  /** Free-form structured data. Avoid PII — no full tokens, just status + length */
  detail: Record<string, unknown>;
}

const STORAGE_KEY = 'trybeup_auth_log';
// Raised from 20 → 60 after a real recurrence on 2026-05-21 showed the cap
// was eating the trigger event. Once the user lands on /login after a silent
// sign-out, every visibility change runs the warmer + every render
// re-probes the durable-storage adapter — together they emit ~9 events per
// page-foreground cycle. Two or three foregrounds was enough to flush the
// actual sign-out cause off the end of a 20-entry log, leaving us with 20
// post-mortem "session still empty" lines and no smoking gun. 60 entries
// gives ~5-6 foreground cycles of headroom before the cause rolls off,
// which is enough to survive the gap between sign-out and the user
// noticing they're on /login.
const MAX_EVENTS = 60;

export function logAuthEvent(type: AuthEventType, detail: Record<string, unknown> = {}): void {
  try {
    const existing = readEvents();
    // Dedupe consecutive identical events. The diagnostic flood that
    // motivated this — `durable_session_restore { outcome: 'both-empty' }`
    // firing on every page render after a sign-out, plus `warmer_run
    // { skipped: true, reason: 'no session or no expires_at' }` on every
    // visibility change — carries no per-occurrence information once it's
    // repeated. Skipping repeats preserves the FIRST occurrence (which is
    // diagnostically meaningful: "this started happening at T") while
    // preventing the trigger events that came BEFORE from being rolled
    // off by post-mortem polling.
    const last = existing.length > 0 ? existing[existing.length - 1] : null;
    if (last && last.type === type && deepEqualDetail(last.detail, detail)) {
      return;
    }
    const next: AuthEvent = {
      at: new Date().toISOString(),
      type,
      detail,
    };
    const trimmed = [...existing, next].slice(-MAX_EVENTS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    /* never throw from a diagnostic */
  }
}

// Shallow-stable JSON compare — detail values are primitives or arrays of
// primitives in every call site, so JSON.stringify gives a deterministic
// fingerprint. Defensive try/catch because diagnostics must never throw.
function deepEqualDetail(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

export function getAuthEvents(): AuthEvent[] {
  return readEvents();
}

export function clearAuthEvents(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function readEvents(): AuthEvent[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as AuthEvent[];
  } catch {
    return [];
  }
}

/**
 * Timestamp written just before we deliberately call authClient.signOut()
 * so the SIGNED_OUT auth-state-change listener can tell "user-initiated /
 * remember-me-expired signout" apart from "SDK cleared the session
 * internally after a refresh-token rejection" — the latter is exactly the
 * scenario we're trying to catch.
 *
 * Persisted via localStorage rather than a module-level variable because
 * the Profile/suspended-account "Log Out" buttons follow `await signOut()`
 * with `window.location.href = '/'`, which can land the SIGNED_OUT
 * listener in the FRESH page's JS context — a module-level boolean set
 * in the previous context would be lost. localStorage survives the
 * navigation, so the flag still resolves correctly post-redirect.
 *
 * Considered stale after 5 seconds so an unrelated later SIGNED_OUT
 * can't piggy-back on an old timestamp.
 */
const SIGNOUT_FLAG_KEY = 'trybeup_signout_self_initiated_at';
const SIGNOUT_FLAG_WINDOW_MS = 5000;

export function markSignoutSelfInitiated(reason: string): void {
  try {
    localStorage.setItem(SIGNOUT_FLAG_KEY, String(Date.now()));
  } catch {
    /* never throw from a diagnostic */
  }
  logAuthEvent('signout_initiated', { reason });
}

export function isSignoutSelfInitiated(): boolean {
  try {
    const raw = localStorage.getItem(SIGNOUT_FLAG_KEY);
    if (!raw) return false;
    const at = parseInt(raw, 10);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < SIGNOUT_FLAG_WINDOW_MS;
  } catch {
    return false;
  }
}
